import React from "react";
import { styled } from "@mui/material/styles";
import { TextField, Typography } from "@mui/material";
import { NODE_UI, stopPropagation } from "./nodeUiStyles";

/**
 * NodeTextField — plain MUI TextField restyled to match the NodeUI
 * shadcn-inspired look (compact, label above the input, muted borders).
 *
 * For arbitrary user text (names, descriptions, free-form values).
 * If the field is a workflow placeholder key, use NodePlaceholderTextField
 * instead — it adds ${...} adornments and sanitises the value.
 */

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    fontSize: NODE_UI.fontSize.value,
    borderRadius: NODE_UI.borderRadiusSm,
    backgroundColor: theme.palette.background.default,
    "& fieldset": {
      borderColor: theme.palette.divider,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.text.secondary,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.text.primary,
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    padding: NODE_UI.input.padding,
    fontSize: NODE_UI.fontSize.value,
  },
  "& .MuiInputBase-multiline": {
    padding: "0",
  },
  "& .MuiInputBase-inputMultiline": {
    padding: NODE_UI.input.padding,
  },
  "& .MuiFormHelperText-root": {
    fontSize: NODE_UI.fontSize.caption,
    marginLeft: 0,
    marginTop: "4px",
  },
}));

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: "4px",
  userSelect: "none",
}));

const NodeTextField = React.memo(function NodeTextField({
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  disabled = false,
  fullWidth = true,
  multiline = false,
  minRows,
  maxRows,
  error = false,
  helperText,
  type = "text",
  inputProps,
  sx,
}) {
  return (
    <div onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation}>
      {label && <Label>{label}</Label>}
      <StyledTextField
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        fullWidth={fullWidth}
        multiline={multiline}
        minRows={minRows}
        maxRows={maxRows}
        error={error}
        helperText={helperText}
        type={type}
        inputProps={inputProps}
        variant="outlined"
        size="small"
        sx={sx}
      />
    </div>
  );
});

export default NodeTextField;
